import styled from "@emotion/styled"
import { CheckCircle, CloudUpload, Download } from "@mui/icons-material"
import { Alert, Box, Button, Container, Paper, Stack, Typography } from "@mui/material"
import axios from "axios"
import html2canvas from "html2canvas"
import { useRef, useState } from "react"

export default function SharePublic() {
	const apiURL = process.env.REACT_APP_API
	const VisuallyHiddenInput = styled("input")({
		clip: "rect(0 0 0 0)",
		clipPath: "inset(50%)",
		height: 1,
		overflow: "hidden",
		position: "absolute",
		bottom: 0,
		left: 0,
		whiteSpace: "nowrap",
		width: 1,
	})

	const [image, setImage] = useState(null)
	const [isProcessed, setIsProcessed] = useState(false)
	const [uploadedImageUrl, setUploadedImageUrl] = useState(null)
	const [generatedBlob, setGeneratedBlob] = useState(null) // Store blob for sharing
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)
	const divRef = useRef(null)

	const handleImageChange = (e) => {
		const file = e.target.files[0]
		if (file) {
			const reader = new FileReader()
			reader.onloadend = () => {
				setImage(reader.result)
				setIsProcessed(false)
				setUploadedImageUrl(null)
				setGeneratedBlob(null)
			}
			reader.readAsDataURL(file)
		}
	}

	const handleProceed = async () => {
		if (!image) return

		setLoading(true)
		setError(null)

		try {
			const element = divRef.current
			const currentWidth = element.offsetWidth
			const scale = 1000 / currentWidth

			// Increase scale for better quality
			const canvas = await html2canvas(element, {
				scale: scale * 2, // Double the scale for higher quality
				useCORS: true,
				allowTaint: true,
				backgroundColor: null,
				imageTimeout: 0,
				logging: false,
			})

			// Convert canvas to Blob instead of base64
			canvas.toBlob(
				async (blob) => {
					if (!blob) {
						setError("Failed to create image. Please try again.")
						setLoading(false)
						return
					}

					// Store the blob for download and sharing
					setGeneratedBlob(blob)

					try {
						// Create FormData and append the blob as binary file
						const formData = new FormData()
						formData.append("img", blob, "diwali_poster.jpg")

						// Upload to backend using axios
						const response = await axios.post(`${apiURL}/public/postToGallery`, formData, {
							headers: {
								"Content-Type": "multipart/form-data",
							},
						})

						console.log("Full Response:", response.data)

						// Check if upload was successful
						if (response.data.status === true && response.data.data?.filename) {
							// Construct the full image URL from the filename
							const imageUrl = `${apiURL}/uploads/gallery/${response.data.data.filename}`

							setUploadedImageUrl(imageUrl)
							setIsProcessed(true)
							console.log("Success! Image URL constructed:", imageUrl)
						} else {
							// Still set processed as true since we have the blob
							setIsProcessed(true)
							console.log("Upload successful, using blob for download/share")
						}
					} catch (err) {
						console.error("Error uploading image:", err)
						// Still allow download/share even if upload fails
						setIsProcessed(true)
						console.log("Upload failed, but using blob for download/share")
					} finally {
						setLoading(false)
					}
				},
				"image/jpeg",
				1.0 // Maximum quality
			)
		} catch (err) {
			console.error("Error processing image:", err)
			setError("Failed to process image. Please try again.")
			setLoading(false)
		}
	}

	const handleDownload = () => {
		if (!generatedBlob) {
			setError("No image available to download.")
			return
		}

		try {
			// Create download link from the stored blob
			const url = URL.createObjectURL(generatedBlob)
			const link = document.createElement("a")
			link.href = url
			link.download = "diwali_poster.jpg"
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)
			URL.revokeObjectURL(url)
		} catch (err) {
			console.error("Download error:", err)
			setError("Failed to download image. Please try again.")
		}
	}

	const handleWhatsAppShare = async () => {
		if (!generatedBlob) {
			setError("No image available to share.")
			return
		}

		try {
			// Check if Web Share API is supported
			if (navigator.share && navigator.canShare) {
				// Create a File object from the blob
				const file = new File([generatedBlob], "diwali_poster.jpg", {
					type: "image/jpeg",
				})

				// Check if we can share files
				if (navigator.canShare({ files: [file] })) {
					await navigator.share({
						files: [file],
						title: "Diwali Poster",
						text: "Check out my personalized Diwali poster!",
					})
					console.log("Shared successfully")
				} else {
					// Fallback: Share with URL if available
					shareWithURL()
				}
			} else {
				// Fallback for browsers that don't support Web Share API
				shareWithURL()
			}
		} catch (err) {
			console.error("Share error:", err)
			// If user cancels, don't show error
			if (err.name !== "AbortError") {
				setError("Failed to share image. Please try downloading instead.")
			}
		}
	}

	const shareWithURL = () => {
		// Fallback method using WhatsApp URL scheme
		if (uploadedImageUrl) {
			const message = `Check out my personalized Diwali poster! ${uploadedImageUrl}`
			const encodedMessage = encodeURIComponent(message)
			const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
			const whatsappUrl = isMobile ? `whatsapp://send?text=${encodedMessage}` : `https://web.whatsapp.com/send?text=${encodedMessage}`
			window.open(whatsappUrl, "_blank")
		} else {
			setError("Please download the image and share manually.")
		}
	}

	return (
		<Box sx={{ minHeight: "100vh", bgcolor: "#f5f5f5", py: { xs: 2, sm: 3, md: 4 } }}>
			<Container maxWidth="xl">
				<Box sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
					<Typography variant="h4" fontWeight={600} gutterBottom sx={{ fontSize: { xs: "1.5rem", sm: "2rem", md: "2.125rem" } }}>
						Diwali Poster Creator
					</Typography>
					<Typography variant="body1" color="text.secondary" sx={{ mb: 2, fontSize: { xs: "0.875rem", sm: "1rem" } }}>
						Upload your image and create your personalized poster
					</Typography>

					{error && (
						<Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
							{error}
						</Alert>
					)}

					<Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
						<Button component="label" variant="contained" startIcon={<CloudUpload />} size="large" fullWidth={{ xs: true, sm: false }} disabled={loading}>
							Select Image
							<VisuallyHiddenInput type="file" accept=".jpeg, .jpg, .png" onChange={handleImageChange} />
						</Button>

						{image && !isProcessed && (
							<Button variant="contained" color="primary" startIcon={<CheckCircle />} size="large" onClick={handleProceed} disabled={loading} fullWidth={{ xs: true, sm: false }}>
								{loading ? "Processing..." : "Proceed"}
							</Button>
						)}

						{isProcessed && generatedBlob && (
							<>
								<Button variant="contained" color="success" startIcon={<Download />} size="large" onClick={handleDownload} fullWidth={{ xs: true, sm: false }}>
									Download Poster
								</Button>
								{/* <Button
									variant="contained"
									sx={{
										bgcolor: "#25D366",
										"&:hover": { bgcolor: "#1fb855" },
										color: "white",
									}}
									startIcon={<WhatsApp />}
									size="large"
									onClick={handleWhatsAppShare}
									fullWidth={{ xs: true, sm: false }}
								>
									Share on WhatsApp
								</Button> */}
							</>
						)}
					</Stack>
				</Box>

				<Box sx={{ display: "flex", justifyContent: "center", mt: { xs: 2, sm: 3, md: 4 } }}>
					<Paper
						elevation={4}
						sx={{
							p: { xs: 1.5, sm: 2, md: 3 },
							bgcolor: "white",
							width: "100%",
							maxWidth: "100%",
						}}
					>
						<Typography
							variant="h6"
							fontWeight={600}
							sx={{
								mb: 2,
								fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" },
							}}
						>
							Poster Preview
						</Typography>
						<Box
							sx={{
								overflow: "auto",
								display: "flex",
								justifyContent: "center",
							}}
						>
							<Box
								ref={divRef}
								sx={{
									position: "relative",
									width: { xs: 320, sm: 500, md: 700, lg: 1000 },
									height: { xs: 320, sm: 500, md: 700, lg: 1000 },
									maxWidth: "100%",
									aspectRatio: "1/1",
								}}
							>
								<img
									src="/images/diwali_website_poster.jpg"
									alt="Diwali poster"
									crossOrigin="anonymous"
									style={{
										width: "100%",
										height: "100%",
										display: "block",
										objectFit: "cover",
									}}
								/>
								{/* Top Right - Uploaded Image */}
								<Box
									sx={{
										position: "absolute",
										top: { xs: "2%", sm: "2.5%", md: "3%" },
										right: { xs: "2%", sm: "2.5%", md: "3%" },
										width: { xs: "25%", sm: "22%", md: "20%" },
										height: { xs: "25%", sm: "22%", md: "20%" },
										border: "3px solid white",
										borderRadius: "12px",
										overflow: "hidden",
										bgcolor: image ? "transparent" : "rgba(255,255,255,0.3)",
										backdropFilter: "blur(10px)",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
									}}
								>
									{image ? (
										<img
											src={image}
											alt="Uploaded"
											crossOrigin="anonymous"
											style={{
												width: "100%",
												height: "100%",
												objectFit: "cover",
												objectPosition: "center",
												imageRendering: "-webkit-optimize-contrast",
											}}
										/>
									) : (
										<Box sx={{ textAlign: "center" }}>
											<CloudUpload
												sx={{
													fontSize: { xs: 32, sm: 40, md: 48 },
													color: "white",
													opacity: 0.7,
													mb: 0.5,
												}}
											/>
											<Typography
												variant="caption"
												sx={{
													color: "white",
													opacity: 0.7,
													display: { xs: "none", sm: "block" },
													fontSize: { sm: "0.65rem", md: "0.75rem" },
												}}
											>
												Upload Image
											</Typography>
										</Box>
									)}
								</Box>
							</Box>
						</Box>
					</Paper>
				</Box>
			</Container>
		</Box>
	)
}
